import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, TextField,
  CircularProgress, Alert, Divider, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions
} from '@mui/material';
import {
  AccountBalanceWallet, TrendingUp, AttachMoney, History
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getTeacherEarnings, requestWithdrawal } from '../../api/earnings';

const Earnings = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadEarnings();
  }, [user?.id]);

  const loadEarnings = async () => {
    if (!user?.id) return;
    try {
      const data = await getTeacherEarnings(user.id);
      setEarnings(data);
    } catch (err) {
      console.error('Error loading earnings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    setError('');
    setWithdrawing(true);
    try {
      await requestWithdrawal(user.id, Number(withdrawAmount), paypalEmail);
      setSuccess('Withdrawal request submitted successfully!');
      setWithdrawDialog(false);
      setWithdrawAmount('');
      setPaypalEmail('');
      await loadEarnings();
    } catch (err) {
      setError(err.message || 'Failed to submit withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Earnings</Typography>

      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', color: 'white' }}>
            <CardContent>
              <AccountBalanceWallet sx={{ mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                ${earnings?.availableBalance?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Available Balance</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <TrendingUp sx={{ mb: 1, color: 'primary.main' }} />
              <Typography variant="h4" fontWeight={700}>
                ${earnings?.netEarnings?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Net Earnings</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <AttachMoney sx={{ mb: 1, color: 'warning.main' }} />
              <Typography variant="h4" fontWeight={700}>
                ${earnings?.totalWithdrawn?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Withdrawn</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <History sx={{ mb: 1, color: 'info.main' }} />
              <Typography variant="h4" fontWeight={700}>
                {earnings?.totalLessons || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Completed Lessons</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Withdraw Button */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Request Withdrawal</Typography>
            <Typography variant="body2" color="text.secondary">
              Minimum withdrawal: $10 · Platform fee: 15% · Payouts via PayPal
            </Typography>
          </Box>
          <Button
            variant="contained" onClick={() => setWithdrawDialog(true)}
            disabled={(earnings?.availableBalance || 0) < 10}
          >
            Withdraw Funds
          </Button>
        </Box>
      </Paper>

      {/* Withdrawal History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Withdrawal History</Typography>
        {earnings?.withdrawals?.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>PayPal</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {earnings.withdrawals.map(w => (
                  <TableRow key={w.id}>
                    <TableCell>
                      {w.createdAt?.toLocaleDateString?.('en') || 'N/A'}
                    </TableCell>
                    <TableCell fontWeight={600}>${w.amount?.toFixed(2)}</TableCell>
                    <TableCell>{w.paypalEmail}</TableCell>
                    <TableCell>
                      <Chip label={w.status} size="small" color={getStatusColor(w.status)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            No withdrawals yet
          </Typography>
        )}
      </Paper>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawDialog} onClose={() => setWithdrawDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Withdrawal</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Available balance: ${earnings?.availableBalance?.toFixed(2) || '0.00'}
          </Typography>
          <TextField
            fullWidth label="Amount ($)" type="number" sx={{ mb: 2 }}
            value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)}
            inputProps={{ min: 10, max: earnings?.availableBalance || 0 }}
          />
          <TextField
            fullWidth label="PayPal Email" type="email"
            value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)}
            placeholder="your@paypal.email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialog(false)}>Cancel</Button>
          <Button
            variant="contained" onClick={handleWithdraw}
            disabled={withdrawing || !withdrawAmount || !paypalEmail}
            startIcon={withdrawing && <CircularProgress size={20} />}
          >
            {withdrawing ? 'Processing...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Earnings;
